// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

import DataTable from "components/Tables/DevicesTable";

import {
    QueryClient,
    QueryClientProvider,
    useQuery,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import bksAPI from 'services/api/bks';
import inspectionAPI from 'modules/inspections/api';
import { calculateBattery, processDevicesData } from "utils/device";
import { getUserAccessLevel } from "utils/setup";

import { useNavigate } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";

import PropTypes from 'prop-types';
import { useEffect } from "react";
import { Box, Container, Grid, Tooltip } from "@mui/material";
import moment from "moment";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import UptimeDoughnutChart from "components/Charts/StatisticCards/UptimeDoughnutChart";

import { wrapper, loadingScreen } from "utils/wrapper";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import NewAssignmentsCard from "components/Cards/NewAssignmentsCard";
import ReportGenerationCard from "components/Cards/ReportGenerationCard";
import { getHealthStatus } from "utils/device";

function DevicesTableView({ map, user }) {
    const entriesPerPage = { defaultValue: 25, entries: [10, 25, 50, 75] };

    disableMap(map);

    // Queries
    bksAPI.loadToken(useAuth0());
    inspectionAPI.loadToken(useAuth0());

    const navigate = useNavigate();
    const [controller] = useSoftUIController();
    const { deviceLayout } = controller;

    const onRowClicked = function (row, event) {
        // console.log(row);
        // console.log(event);
        goToDevicePage(row.original.uuid);
    }

    const goToDevicePage = (uuid) => {
        let path = '../../devices/' + uuid;
        // console.log(navigate)
        navigate(path);
    }

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await bksAPI.getAllDevices()
        },
        // staleTime: 30 * 1000
    });

    const all_inspections = useQuery({
        queryKey: ['getAllInspectionsForUser', user],
        queryFn: async () => {
            return await inspectionAPI.getAllInspectionsForUser(user);
        }
    });

    if (devices.isLoading || all_inspections.isLoading) {
        return loadingScreen(deviceLayout, true);
    }

    if (devices.error || all_inspections.error) {
        console.log(devices.error, all_inspections.error)
        return "An error has occurred";
    }

    const devicesData = processDevicesData(devices.data, getUserAccessLevel(user.user.user_type, 'technician'));

    var data_table_data = {
        columns: [], rows: devicesData.map((row) => {
            row.accessor_id = row.stop != null ? (row.stop?.stop_id ? row.stop.stop_id : row.stop.site_id) : "N/A";
            return row;
        })
    };

    if (getUserAccessLevel(user.user.user_type, 'technician')) {
        data_table_data.columns = [
            { Header: 'Name', accessor: 'name' },
            { Header: 'Site/Stop ID', accessor: 'accessor_id' },
            { Header: 'Battery', accessor: 'battery' },
            { Header: 'Temperature', accessor: 'temperature' },
            { Header: 'Humidity', accessor: 'humidity' },
            { Header: 'RSSI', accessor: 'rssi' },
            { Header: 'Serial Number', accessor: 'serial_number' },
            {
                Header: 'Last Updated',
                accessor: 'last_updated',
                sortType: function (rowa, rowb) {
                    if (rowa.original.last_heartbeat > rowb.original.last_heartbeat) {
                        return 1;
                    }
                    else if (rowa.original.last_heartbeat < rowb.original.last_heartbeat) {
                        return -1;
                    }
                    else {
                        return 0;
                    }
                }
            }
        ];
    }
    else {
        data_table_data.columns = [
            { Header: 'Name', accessor: 'name' },
            { Header: 'Site/Stop ID', accessor: 'accessor_id' },
            { Header: 'Battery', accessor: 'battery' },
            { Header: 'Temperature', accessor: 'temperature' },
            { Header: 'Serial Number', accessor: 'serial_number' },
            {
                Header: 'Health Status', accessor: 'health_status', Cell: ({ cell }) => {
                    var health_status = getHealthStatus(cell.row.original, user.user.user_type);
                    return (
                        <SoftTypography
                            color={health_status.color}
                            textGradient
                            sx={{ fontSize: '14px', fontWeight: 'bold', verticalAlign: "middle" }}
                        >
                            {health_status.text}
                        </SoftTypography>
                    )
                }
            }
        ];
    }

    const devicesTable = (
        <Card sx={{ height: '100%', width: "100%" }}>
            <SoftBox p={2} lineHeight={1}>
                <SoftBox display='flex' flexDirection='row' alignItems='flex-start'>
                    <SoftTypography px={1} variant="h4">
                        All Devices
                    </SoftTypography>
                    {getUserAccessLevel(user.user.user_type, 'technician') && (
                        <Tooltip title={<div style={{ textAlign: 'left' }}>Red - Outdated/Offline<br />Yellow - Requires Attention<br />Green - Healthy</div>} placement="right" sx={{ paddingTop: '7px' }}>
                            <HelpOutlineIcon fontSize="small" />
                        </Tooltip>
                    )}
                    {(getUserAccessLevel(user.user.user_type, 'viewer') && !getUserAccessLevel(user.user.user_type, 'technician')) && (
                        <Tooltip title={<div style={{ textAlign: 'left' }}>Red - Requires Attention<br />Green - Healthy</div>} placement="right" sx={{ paddingTop: '7px' }}>
                            <HelpOutlineIcon fontSize="small" />
                        </Tooltip>
                    )}
                </SoftBox>
                <DataTable table={data_table_data} entriesPerPage={entriesPerPage} user={user} onRowClicked={(row, event) => onRowClicked(row, event)} canSearch />
            </SoftBox>
        </Card>
    );
    return (
        wrapper(
            (
                <Grid container spacing={deviceLayout == 'mobile' ? 1 : 2}>
                    {getUserAccessLevel(user.user.user_type, 'technician') ? (
                        <>
                            <Grid item xxs={12} md={6} xl={user.user.organisations.indexOf('translink-poc') > -1 ? 4 : 6}>
                                <NewAssignmentsCard assignment_count={all_inspections.data.inspections.filter((row) => row.status == "unresolved").length} />
                            </Grid>
                            <Grid item xxs={12} md={6} xl={user.user.organisations.indexOf('translink-poc') > -1 ? 4 : 6}>
                                <UptimeDoughnutChart
                                    icon="percent"
                                    color="secondary"
                                    title="All Devices"
                                    devices={devices.data}
                                    user={user}
                                    height={deviceLayout == 'mobile' ? '100px' : 'unset'}
                                    maxHeight='180px'
                                />
                            </Grid>
                            {user.user.organisations.indexOf('translink-poc') > -1 && (
                                <Grid item xxs={12} md={12} xl={4}>
                                    <ReportGenerationCard devices={devicesData} deviceLayout={deviceLayout} />
                                </Grid>
                            )}
                        </>
                    ) : (
                        <>
                            <Grid item xxs={12} md={user.user.organisations.indexOf('translink-poc') > -1 ? 6 : 12}>
                                <UptimeDoughnutChart
                                    icon="percent"
                                    color="secondary"
                                    title="All Devices"
                                    devices={devices.data}
                                    user={user}
                                    height={deviceLayout == 'mobile' ? '100px' : 'unset'}
                                    maxHeight='180px'
                                />
                            </Grid>
                            {user.user.organisations.indexOf('translink-poc') > -1 && (
                                <Grid item xxs={12} md={6}>
                                    <ReportGenerationCard devices={devicesData} deviceLayout={deviceLayout} />
                                </Grid>
                            )}
                        </>
                    )}
                    <Grid item xxs={12}>
                        {devicesTable}
                    </Grid>
                </Grid >
            ), deviceLayout
        )
    );
}

DevicesTableView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default DevicesTableView;
